import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { LoginError } from 'src/app/core/_enums/login.error';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { MailService } from 'src/app/core/_services/mail.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { AuthService } from '../../core/_services/auth.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  formLogin!: FormGroup;
  formReset!: FormGroup;
  loading = false;
  showErrorMsg = false;
  wrongPassword = false;
  noUsableRight = false;
  accountIsLocked = false;
  currentForm = 'login';
  showPassword = false;
  ssoMessageKey = '';
  resetError = false;
  pendingBannerKey = '';

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private mailService: MailService,
    private snackbarService: SnackbarService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.pendingBannerKey = this.pendingBanner(history.state?.inscriptionPending);
    this.ssoMessageKey = this.ssoMessage(this.activeRoute.snapshot.queryParamMap.get('sso'));

    this.formLogin = this.formBuilder.group({
      login: ['', Validators.required],
      password: ['', Validators.required],
      rememberMe: [false]
    });

    this.ResetForm();
  }

  ssoMessage(reason: string | null): string {
    switch (reason) {
      case 'unknown':
        return 'sso_error_unknown_account';
      case 'ambiguous':
        return 'sso_error_ambiguous_account';
      case 'no-access':
        return 'sso_error_no_access';
      case 'locked':
        return 'login_error_account_locked';
      case 'token':
        return 'sso_error_token';
      default:
        return '';
    }
  }

  pendingBanner(pending: string | undefined): string {
    switch (pending) {
      case 'new-organisation':
        return 'login_pending_banner';
      case 'referent':
        return 'login_pending_banner_referent';
      case 'fntp':
        return 'login_pending_banner_fntp';
      default:
        return '';
    }
  }

  ResetForm(): void {
    this.formReset = this.formBuilder.group({
      email: ['', Validators.compose([Validators.required, Validators.email])]
    });
  }

  get frmLogin() {
    return this.formLogin.controls;
  }

  get frmReset() {
    return this.formReset.controls;
  }

  onSubmitLogin() {
    // stop here if form is invalid
    if (this.formLogin.invalid) {
      return;
    }

    this.loading = true;
    this.accountIsLocked = false;
    this.showErrorMsg = false;
    this.wrongPassword = false;
    this.noUsableRight = false;

    this.authService
      .login(this.frmLogin.login.value, this.frmLogin.password.value, this.frmLogin.rememberMe.value)
      .pipe(first())
      .subscribe(
        (data) => {
          if (!data.success) {
            if (data.error === LoginError.AccountIsLocked) {
              this.accountIsLocked = true;
            } else if (data.error === LoginError.InvalidPassword) {
              this.wrongPassword = true;
            } else if (data.error === LoginError.NoUsableRight) {
              this.noUsableRight = true;
            } else {
              this.showErrorMsg = true;
            }
            this.loading = false;
            return;
          }
          this.loading = false;
          this.router.navigate(['/']);
        },
        () => {
          this.showErrorMsg = true;
          this.loading = false;
        }
      );
  }

  sendReinitPassword() {
    const formData = this.formReset.value;
    if (formData.email) {
      this.mailService.sendMailReset(formData.email).subscribe(
        () => {
          this.snackbarService.open(SnackbarTypes.mailReinitPasswordSend);
          this.currentForm = 'login';
          this.resetError = false;
        },
        () => {
          this.resetError = true;
          this.snackbarService.open(SnackbarTypes.errorUserNotFound);
        }
      );
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}
